import React, { useState, useEffect } from "react";

import { ArrowRight } from "lucide-react";

import { useNavigate } from "react-router-dom";

import ThemedSelect from "./components/ThemedSelect";
import AppHeader from "./components/AppHeader";
import { apiUrl, fetchJson } from "./api";
import { supabase } from "./lib/supabase";

const AI_PROVIDER = "bedrock";

const DeepToast = () => {

  const [userId, setUserId] = useState("");

  const [course, setCourse] = useState("");

  const [module, setModule] = useState("");

  const [numQuestions, setNumQuestions] = useState("");

  const [mcqSplit, setMcqSplit] = useState("");

  const [tfSplit, setTfSplit] = useState("");

  const [easyPct, setEasyPct] = useState("34");

  const [mediumPct, setMediumPct] = useState("33");

  const [hardPct, setHardPct] = useState("33");

  const [errors, setErrors] = useState({});

  const [courseOptions, setCourseOptions] = useState([]);

  const [modules, setModules] = useState([]);

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUserId(data.session?.user?.id || "");
    });
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [userId]);

 

  // Fetch modules when course changes

  useEffect(() => {

    if (course) {

      fetchModules(course);

      setModule(""); // Reset module when course changes

    } else {

      setModules([]);

    }

  }, [course]);

 

  // Fetch courses from backend

  const fetchCourses = async () => {

    if (!userId) {
      setCourseOptions([]);
      setLoading(false);
      return;
    }

    try {

      setLoading(true);

      const data = await fetchJson(
        apiUrl(`/api/user/extracted-courses?userId=${encodeURIComponent(userId)}`),
        {},
        25000
      );

      const opts = (data.courses || []).map((c) => ({
        label: c.name || String(c.id),
        value: String(c.id),
      }));
      setCourseOptions(opts);

    } catch (error) {

      console.error('Error fetching courses:', error);

      setCourseOptions([]);

    } finally {

      setLoading(false);

    }

  };

 

  const fetchModules = async (canvasCourseId) => {

    if (!userId || !canvasCourseId) {
      setModules([]);
      return;
    }

    try {

      setLoading(true);

      const data = await fetchJson(
        apiUrl(
          `/api/user/extracted-modules?userId=${encodeURIComponent(
            userId
          )}&canvasCourseId=${encodeURIComponent(canvasCourseId)}`
        ),
        {},
        25000
      );

      setModules(data.modules || []);

    } catch (error) {

      console.error('Error fetching modules:', error);

      setModules([]);

    } finally {

      setLoading(false);

    }

  };

 

  useEffect(() => {

    const savedData = localStorage.getItem("formData");

    if (savedData) {

      try {

        const data = JSON.parse(savedData);

        setNumQuestions(data.numQuestions || "");

        setMcqSplit(data.mcqSplit || "");

        setTfSplit(data.tfSplit || "");
        if (data.easyPct !== undefined) setEasyPct(String(data.easyPct));
        if (data.mediumPct !== undefined) setMediumPct(String(data.mediumPct));
        if (data.hardPct !== undefined) setHardPct(String(data.hardPct));
        if (data.course) {
          setCourse(String(data.course));
        }
      } catch (_) {
        // ignore invalid saved form data
      }
    }
  }, []);

  useEffect(() => {

    if (!userId || courseOptions.length === 0) return;

    const savedData = localStorage.getItem("formData");

    if (!savedData) return;

    try {

      const data = JSON.parse(savedData);

      const ok = data.course && courseOptions.some((o) => String(o.value) === String(data.course));

      if (ok) {
        setCourse(String(data.course));

        setModule(data.module || "");
      }

    } catch (_) {}

  }, [courseOptions, userId]);

 

  // Use pagehide to clear saved data on a non-cached page unload (such as a refresh)

  useEffect(() => {

    const handlePageHide = (e) => {

      // e.persisted will be true if the page is cached (back navigation)

      if (!e.persisted) {

        localStorage.removeItem("formData");

      }

    };

    window.addEventListener("pagehide", handlePageHide);

    return () => window.removeEventListener("pagehide", handlePageHide);

  }, []);

 

  const difficultySum = (parseInt(easyPct || "0", 10) || 0)
    + (parseInt(mediumPct || "0", 10) || 0)
    + (parseInt(hardPct || "0", 10) || 0);

  const clampPct = (v) => {
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) return "";
    if (n < 0) return "0";
    if (n > 100) return "100";
    return String(n);
  };

  const handleNextPage = () => {

    const newErrors = {};

    if (!course) newErrors.course = "Please select a course!";

    if (!module) newErrors.module = "Please select a module!";

    if (!numQuestions) newErrors.numQuestions = "Please select number of questions!";

    if (!mcqSplit) newErrors.mcqSplit = "Please select Multiple choice questions percentage!";

    if (difficultySum !== 100) newErrors.difficulty = `Easy + Medium + Hard must total 100% (currently ${difficultySum}%).`;

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {

      const sel = courseOptions.find((o) => String(o.value) === String(course));

      const formData = {
        course,
        courseDisplayName: sel?.label || course,
        module,
        numQuestions,
        mcqSplit,
        tfSplit,
        easyPct: parseInt(easyPct, 10) || 0,
        mediumPct: parseInt(mediumPct, 10) || 0,
        hardPct: parseInt(hardPct, 10) || 0,
        userId,
        aiProvider: AI_PROVIDER,
      };

      localStorage.setItem("formData", JSON.stringify(formData));

      navigate("/summary");

    }

  };

 

  return (

    <div className="min-h-screen w-full bg-secondary">

      {/* Header */}

      <AppHeader titleSize="text-3xl" />

      {/* Banner */}

      <div className="bg-accent text-center py-4">

        <h2 className="text-2xl font-bold text-primary underline">Choose your options for generating questions</h2>

      </div>

      {/* Form */}

      <div className="px-12 py-8">

        {/* Select Course */}

        <div className="mb-6">

           <p className="italic font-bold text-sm text-red-500 mb-4">
          * All fields are required
          </p>

          <label className="block text-lg font-semibold mb-2">Select Course</label>

          <ThemedSelect
            className="w-3/4"
            value={course}
            onChange={(v) => {
              setCourse(v);
              if (v)
                setErrors((prev) => ({ ...prev, course: "" }));
            }}
            options={courseOptions}
            placeholder={userId ? "Select a course you have extracted" : "Sign in to see your courses"}
            disabled={loading || !userId}
          />

          {errors.course && <p className="text-red-500 text-sm mt-1">{errors.course}</p>}

          {loading && <p className="text-blue-500 text-sm mt-1">Loading courses...</p>}

          {!loading && userId && courseOptions.length === 0 ? (
            <p className="text-amber-700 text-sm mt-2 max-w-xl">
              No extracted courses yet. Open <strong>Onboarding</strong>, run a Canvas extraction, then return here.
              Only courses with stored module content appear in this list.
            </p>
          ) : null}

        </div>

 

        {/* Select Module */}

        <div className="mb-6">

          <label className="block text-lg font-semibold mb-2">Select Module</label>

          <ThemedSelect
            className="w-3/4"
            value={module}
            onChange={(v) => {
              setModule(v);
              if (v)
                setErrors((prev) => ({ ...prev, module: "" }));
            }}
            options={modules}
            placeholder="Click here to select a module"
            disabled={!course || loading}
          />

          {errors.module && <p className="text-red-500 text-sm mt-1">{errors.module}</p>}

          {loading && course && <p className="text-blue-500 text-sm mt-1">Loading modules...</p>}

        </div>

 

        {/* Select Number of Questions */}

        <div className="mb-6">

          <label className="block text-lg font-semibold mb-2">

            Select number of questions you want to generate

          </label>

          <ThemedSelect
            className="w-3/4"
            value={numQuestions}
            onChange={(v) => {
              setNumQuestions(v);
              if (v)
                setErrors((prev) => ({ ...prev, numQuestions: "" }));
            }}
            options={["10","20","30","40","50"]}
            placeholder="Choose the number of questions to be generated"
          />

          {errors.numQuestions && (

            <p className="text-red-500 text-sm mt-1">{errors.numQuestions}</p>

          )}

        </div>

 

        {/* Question Split */}

        <div className="mb-6">

 

          <label className="block text-lg font-semibold mb-2">Select the question split between Multiple Choice Questions & True/False</label>

 

          <div className="flex space-x-3">

 

          {/* MCQ select and error */}

          <div className="flex flex-col w-[37%]">

            <ThemedSelect
              value={mcqSplit}
              onChange={(v) => {
                setMcqSplit(v);
                setTfSplit(v ? (100 - parseInt(v, 10)).toString() : "");
                if (v) {
                  setErrors((prev) => ({ ...prev, mcqSplit: "" }));
                }
              }}
              options={[
                { label: "10%", value: "10" },
                { label: "20%", value: "20" },
                { label: "30%", value: "30" },
                { label: "40%", value: "40" },
                { label: "50%", value: "50" },
                { label: "60%", value: "60" },
                { label: "70%", value: "70" },
                { label: "80%", value: "80" },
                { label: "90%", value: "90" },
                { label: "100%", value: "100" },
              ]}
              placeholder="Multiple Choice Questions"
            />

            {errors.mcqSplit && (

              <p className="text-red-500 text-sm mt-1">{errors.mcqSplit}</p>

            )}

          </div>

 

          {/* TF Split input */}

          <div className="flex flex-col w-[37%]">

            <input
              type="text"
              className="h-12 px-4 text-lg border rounded border-accent/40 bg-accent/10 text-primary focus:outline-none"
              value={tfSplit ? `${tfSplit}%` : "True/False"}
              readOnly
            />

          </div>

        </div>

 

        </div>

 

        {/* Difficulty Split */}

        <div className="mb-6">

          <label className="block text-lg font-semibold mb-2">

            Difficulty mix (must total 100%)

          </label>

          <p className="text-sm text-gray-600 mb-3 italic">

            Easy &amp; Medium questions are generated by Gemini (longer, more descriptive). Hard questions are generated by Claude (sharper, analytical).

          </p>

          <div className="flex flex-col sm:flex-row gap-3 max-w-3xl">

            <div className="flex flex-col flex-1">

              <label className="text-sm font-medium mb-1 text-primary">Easy %</label>

              <input

                type="number"

                min="0"

                max="100"

                value={easyPct}

                onChange={(e) => {

                  setEasyPct(clampPct(e.target.value));

                  setErrors((prev) => ({ ...prev, difficulty: "" }));

                }}

                className="h-12 px-4 text-lg border rounded border-accent/40 bg-accent/10 text-primary focus:outline-none focus:ring-2 focus:ring-accent"

              />

              <span className="text-xs text-gray-500 mt-1">Gemini · short recall</span>

            </div>

            <div className="flex flex-col flex-1">

              <label className="text-sm font-medium mb-1 text-primary">Medium %</label>

              <input

                type="number"

                min="0"

                max="100"

                value={mediumPct}

                onChange={(e) => {

                  setMediumPct(clampPct(e.target.value));

                  setErrors((prev) => ({ ...prev, difficulty: "" }));

                }}

                className="h-12 px-4 text-lg border rounded border-accent/40 bg-accent/10 text-primary focus:outline-none focus:ring-2 focus:ring-accent"

              />

              <span className="text-xs text-gray-500 mt-1">Gemini · application</span>

            </div>

            <div className="flex flex-col flex-1">

              <label className="text-sm font-medium mb-1 text-primary">Hard %</label>

              <input

                type="number"

                min="0"

                max="100"

                value={hardPct}

                onChange={(e) => {

                  setHardPct(clampPct(e.target.value));

                  setErrors((prev) => ({ ...prev, difficulty: "" }));

                }}

                className="h-12 px-4 text-lg border rounded border-accent/40 bg-accent/10 text-primary focus:outline-none focus:ring-2 focus:ring-accent"

              />

              <span className="text-xs text-gray-500 mt-1">Claude · analytical</span>

            </div>

            <div className="flex flex-col flex-1">

              <label className="text-sm font-medium mb-1 text-primary">Total</label>

              <input

                type="text"

                readOnly

                value={`${difficultySum}%`}

                className={`h-12 px-4 text-lg border rounded focus:outline-none ${

                  difficultySum === 100

                    ? "border-green-500 bg-green-50 text-green-700"

                    : "border-red-400 bg-red-50 text-red-700"

                }`}

              />

              <span className="text-xs text-gray-500 mt-1">Must equal 100%</span>

            </div>

          </div>

          {errors.difficulty && (

            <p className="text-red-500 text-sm mt-2">{errors.difficulty}</p>

          )}

        </div>


        {/* Next Page Button */}

        <div className="flex justify-start mt-10">

          <button

            onClick={handleNextPage}

            className="flex items-center gap-2 bg-primary text-white hover:opacity-90 font-semibold py-3 px-6 rounded transition"

          >

            Continue

            <ArrowRight className="w-5 h-5" />

          </button>

        </div>

      </div>

    </div>

 );

};

 

export default DeepToast;
